/**
 * NuGuard AI - Centralized Logger Service
 *
 * Structured logging for errors, API calls, and user actions.
 * For user-facing error message translation, see `utils/errorHandler.ts`.
 */

import { User } from '../types';

class LoggerService {
  private static instance: LoggerService;

  private constructor() {}

  public static getInstance(): LoggerService {
    if (!LoggerService.instance) {
      LoggerService.instance = new LoggerService();
    }
    return LoggerService.instance;
  }

  /**
   * Log user action for audit trail
   * Note: Actual audit logs are created server-side by the backend
   */
  public async log(user: User, action: string, details: string) {
    console.log(`[AUDIT] ${new Date().toISOString()} - ${user.email} - ${action}: ${details}`);
  }

  /**
   * Log informational message to console
   */
  public info(message: string, context?: Record<string, unknown>) {
    const contextStr = context ? ` ${JSON.stringify(context)}` : '';
    console.log(`[INFO] ${new Date().toISOString()} - ${message}${contextStr}`);
  }

  /**
   * Log warning message to console
   */
  public warn(message: string, context?: Record<string, unknown>) {
    const contextStr = context ? ` ${JSON.stringify(context)}` : '';
    console.warn(`[WARN] ${new Date().toISOString()} - ${message}${contextStr}`);
  }

  /**
   * Log error with structured context.
   * Accepts either a thrown error, a context object, or both:
   *   logger.error(message, err)
   *   logger.error(message, contextObject)
   *   logger.error(message, err, contextObject)
   */
  public error(message: string, errorOrContext?: unknown, maybeContext?: Record<string, unknown>) {
    let err: unknown = errorOrContext;
    let context: Record<string, unknown> | undefined = maybeContext;

    // A plain object passed as the 2nd arg (with no 3rd arg) is the context, not an error
    // — unless it carries a `message` field, i.e. it's an error-like object that didn't extend `Error`.
    if (
      maybeContext === undefined &&
      typeof errorOrContext === 'object' &&
      errorOrContext !== null &&
      !(errorOrContext instanceof Error) &&
      !('message' in errorOrContext)
    ) {
      err = undefined;
      context = errorOrContext as Record<string, unknown>;
    }

    const errMessage = err instanceof Error ? err.message : err !== undefined ? String(err) : undefined;
    const stack = err instanceof Error ? err.stack : undefined;
    let contextStr: string | undefined;
    if (context && Object.keys(context).length > 0) {
      try {
        contextStr = JSON.stringify(context);
      } catch {
        contextStr = '[unserializable context]';
      }
    }
    const detail = [errMessage, contextStr].filter(Boolean).join(' ');
    const meta: Record<string, unknown> = { ...context };
    if (stack !== undefined) meta.stack = stack;
    // Keep the format-string arg static so tainted `message`/`detail` content can't be
    // parsed for %s/%d/%j specifiers and used to splice `meta` into the rendered output.
    console.error('[ERROR]', `${new Date().toISOString()} - ${message}${detail ? `: ${detail}` : ''}`, meta);
  }
}

export const logger = LoggerService.getInstance();

/**
 * Log a user-initiated action for analytics.
 */
export function logUserAction(action: string, metadata?: Record<string, unknown>): void {
  console.info(`[ACTION] ${new Date().toISOString()} - ${action}`, metadata ?? {});
}
